import React, { useState, createElement } from 'react';
import { Download, ChevronDown, ChevronUp } from 'lucide-react';
import { FieldWithSources, fieldRequirementsData } from '../data/fieldRequirements';
import { getUniqueFeatures } from '../data/fieldRequirements';
interface FieldsTableProps {
  fields: FieldWithSources[];
  vmFields: FieldWithSources[];
  otherFields: FieldWithSources[];
  selectedProducts: string[];
  selectedFeatures: string[];
  availableFeatures: string[];
  showTabs: boolean;
}
export function FieldsTable({
  fields,
  vmFields,
  otherFields,
  selectedProducts,
  selectedFeatures,
  availableFeatures,
  showTabs
}: FieldsTableProps) {
  const [activeTab, setActiveTab] = useState<'other' | 'vm'>('other');
  const [showOptional, setShowOptional] = useState(false);
  const [showGiftData, setShowGiftData] = useState(true);
  const [showConstituentData, setShowConstituentData] = useState(true);
  // Check if we're in VM-only mode (only VM selected)
  const isVMOnlyMode = selectedProducts.length === 1 && selectedProducts[0] === 'VM';
  // Check if only feature-dependent products are selected without features
  const featureDependentProducts = ['Events', 'Outreach', 'Online Giving'];
  const productLevelProducts = ['Wealth', 'GO', 'VM', 'VM Reimagined'];
  const hasOnlyFeatureDependentProducts = selectedProducts.length > 0 && selectedProducts.every(p => featureDependentProducts.includes(p));
  const hasProductLevelProducts = selectedProducts.some(p => productLevelProducts.includes(p));
  const shouldShowFeatureMessage = hasOnlyFeatureDependentProducts && selectedFeatures.length === 0 && !hasProductLevelProducts;
  // Check if Smart Ask Amounts is selected
  const hasSmartAskAmounts = selectedProducts.includes('Online Giving') && (selectedFeatures.includes('Smart Ask Amounts') || selectedFeatures.length === 0);
  if (selectedProducts.length === 0) {
    return <div className="text-center py-8 sm:py-12 bg-white rounded-lg border border-gray-200" data-id="element-42" data-mp="3fqx9p8q257dm">
        <p className="text-gray-500 text-sm sm:text-base px-4" data-id="element-43" data-mp="1jnr4dv67bl81">
          Select one or more products to view field requirements
        </p>
      </div>;
  }
  if (shouldShowFeatureMessage) {
    const featuresByProduct: Record<string, string[]> = {};
    selectedProducts.forEach(product => {
      const productFeatures = getUniqueFeatures([product]);
      if (productFeatures.length > 0) {
        featuresByProduct[product] = productFeatures;
      }
    });
    return <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-8" data-id="element-44" data-mp="15nziz9dcv2k0">
        <div className="max-w-2xl" data-id="element-45" data-mp="1ao41f4xtq7cv">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4" data-id="element-46" data-mp="1amt15f3l7hqi">
            Feature Selection Required
          </h3>
          <p className="text-sm sm:text-base text-gray-700 mb-6" data-id="element-47" data-mp="1encvyc0ou99h">
            Your selection has specific features which require data to enable.
            Please select feature(s) to see what is required and recommended.
          </p>

          <div className="space-y-4" data-id="element-48" data-mp="ehslz84ks9nm">
            {Object.entries(featuresByProduct).map(([product, features]) => <div key={product} data-id="element-49" data-mp="agqjmifd3hhf">
                <h4 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-50" data-mp="agh1fh0k6ema">
                  {product}
                </h4>
                <ul className="list-disc list-inside space-y-1 ml-2" data-id="element-51" data-mp="pvy5g9xwlx1">
                  {features.map(feature => <li key={feature} className="text-sm text-gray-700" data-id="element-52" data-mp="3rwoy6gszyezz">
                      {feature}
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </div>;
  }
  if (fields.length === 0) {
    return <div className="text-center py-8 sm:py-12 bg-white rounded-lg border border-gray-200" data-id="element-53" data-mp="3dwnyv5sdrfmm">
        <p className="text-gray-500 text-sm sm:text-base px-4" data-id="element-54" data-mp="1h3ueqdt9sdhc">
          No fields match your current selection
        </p>
      </div>;
  }
  // Determine which fields to display based on tabs and mode
  const displayFields = showTabs ? activeTab === 'vm' ? vmFields : otherFields : isVMOnlyMode ? vmFields : fields;
  // Helper function to get required, recommended, and optional sources for a field
  const getSourcesByStatus = (field: FieldWithSources): {
    required: string[];
    recommended: string[];
    optional: string[];
  } => {
    const required: string[] = [];
    const recommended: string[] = [];
    const optional: string[] = [];
    // Filter sources based on active tab or VM-only mode
    const relevantSources = showTabs && activeTab === 'vm' || isVMOnlyMode ? field.sources.filter(source => source === 'VM' || source.startsWith('VM -')) : field.sources;
    // Look up each source in the original data to determine its status
    relevantSources.forEach(source => {
      // Find matching records in the original data
      const matchingRecords = fieldRequirementsData.filter(record => {
        // For GO, VM, and Wealth - source is just the product name
        const recordSource = record.product === 'GO' || record.product === 'VM' || record.product === 'Wealth' || record.product === 'VM Reimagined' ? record.product : `${record.product} - ${record.feature === 'GC Wealth Alerts' ? 'Wealth Alerts' : record.feature === 'GC GO' ? 'GO' : record.feature}`;
        return record.field === field.field && record.section === field.section && recordSource === source;
      });
      // Check status of matching records
      const isRequired = matchingRecords.some(record => record.status === 'Required');
      const isRecommended = matchingRecords.some(record => record.status === 'Recommended');
      const isOptional = matchingRecords.some(record => record.status.startsWith('Optional'));
      if (isRequired) {
        required.push(source);
      } else if (isRecommended) {
        recommended.push(source);
      } else if (isOptional) {
        optional.push(source);
      }
    });
    return {
      required,
      recommended,
      optional
    };
  };
  // Helper function to check if a field is a Constituent field
  const isConstituentField = (field: FieldWithSources) => field.section.startsWith('Constituent');
  // Separate fields into Constituent, Gift Data, and Other
  const constituentFields = displayFields.filter(field => isConstituentField(field));
  const giftDataFields = displayFields.filter(field => field.section === 'Gift Data');
  const otherSectionFields = displayFields.filter(field => !isConstituentField(field) && field.section !== 'Gift Data');
  // Separate Constituent fields into required/recommended and optional
  const constituentRequiredRecommended = constituentFields.filter(field => {
    const {
      required,
      recommended
    } = getSourcesByStatus(field);
    return required.length > 0 || recommended.length > 0;
  });
  const constituentOptional = constituentFields.filter(field => {
    const {
      required,
      recommended,
      optional
    } = getSourcesByStatus(field);
    return required.length === 0 && recommended.length === 0 && optional.length > 0;
  });
  // Separate Other section fields into required/recommended and optional
  const otherRequiredRecommended = otherSectionFields.filter(field => {
    const {
      required,
      recommended
    } = getSourcesByStatus(field);
    return required.length > 0 || recommended.length > 0;
  });
  const otherOptionalFields = otherSectionFields.filter(field => {
    const {
      required,
      recommended,
      optional
    } = getSourcesByStatus(field);
    return required.length === 0 && recommended.length === 0 && optional.length > 0;
  });
  // Separate Gift Data fields into required/recommended and optional
  const giftDataRequiredRecommended = giftDataFields.filter(field => {
    const {
      required,
      recommended
    } = getSourcesByStatus(field);
    return required.length > 0 || recommended.length > 0;
  });
  const giftDataOptional = giftDataFields.filter(field => {
    const {
      required,
      recommended,
      optional
    } = getSourcesByStatus(field);
    return required.length === 0 && recommended.length === 0 && optional.length > 0;
  });
  // Count unique fields that have at least one required source
  const requiredCount = displayFields.filter(field => {
    const {
      required
    } = getSourcesByStatus(field);
    return required.length > 0;
  }).length;
  // Count unique fields that have at least one recommended source
  const recommendedCount = displayFields.filter(field => {
    const {
      recommended
    } = getSourcesByStatus(field);
    return recommended.length > 0;
  }).length;
  // Count optional fields (all optional, not just non-gift-data)
  const optionalCount = displayFields.filter(field => {
    const {
      required,
      recommended,
      optional
    } = getSourcesByStatus(field);
    return required.length === 0 && recommended.length === 0 && optional.length > 0;
  }).length;
  const handleDownloadCSV = () => {
    const fieldsToExport = showTabs ? [...otherFields, ...vmFields] : fields;
    const headers = ['Section', 'Field', 'Required For', 'Recommended For', 'Optional For'];
    const rows = fieldsToExport.map(field => {
      const {
        required,
        recommended,
        optional
      } = getSourcesByStatus(field);
      return [field.section, field.field, required.join(', '), recommended.join(', '), optional.join(', ')];
    });
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `field-requirements-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  // Mobile card component for a single field
  const MobileFieldCard = ({
    field,
    isOptionalSection = false
  }: {
    field: FieldWithSources;
    isOptionalSection?: boolean;
  }) => {
    const {
      required,
      recommended,
      optional
    } = getSourcesByStatus(field);
    return <div className="bg-white border-b border-gray-200 p-4" data-id="element-55" data-mp="32cpak134wu2v">
        <div className="mb-2" data-id="element-56" data-mp="32ejt58qxoa3s">
          <span className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-57" data-mp="2yakeywt4lndl">
            {field.section}
          </span>
        </div>
        <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-58" data-mp="2tcsmo1okjz8v">
          {field.field}
        </h4>

        {!isOptionalSection && <>
            {required.length > 0 && <div className="mb-2" data-id="element-59" data-mp="rlfnlt9a8lkq">
                <span className="text-xs font-medium text-gray-600 block mb-1" data-id="element-60" data-mp="rib9o460mbgv">
                  Required for:
                </span>
                <div className="flex flex-wrap gap-1" data-id="element-61" data-mp="142qbdu49rvex">
                  {required.map(source => <span key={source} className="inline-flex items-center px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded" data-id="element-62" data-mp="18t6sppb7o16k">
                      {source}
                    </span>)}
                </div>
              </div>}

            {recommended.length > 0 && <div className="mb-2" data-id="element-63" data-mp="q38nri2pmo75">
                <span className="text-xs font-medium text-gray-600 block mb-1" data-id="element-64" data-mp="q25xncd7opsa">
                  Recommended for:
                </span>
                <div className="flex flex-wrap gap-1" data-id="element-65" data-mp="u2pra5yktb9o">
                  {recommended.map(source => <span key={source} className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded" data-id="element-66" data-mp="21kqe9fva5rbq">
                      {source}
                    </span>)}
                </div>
              </div>}
          </>}

        {isOptionalSection && optional.length > 0 && <div data-id="element-67" data-mp="ah2khgqcgrqe">
            <span className="text-xs font-medium text-gray-600 block mb-1" data-id="element-68" data-mp="agkqyrv2grm6">
              Optional for:
            </span>
            <div className="flex flex-wrap gap-1" data-id="element-69" data-mp="3ixfm1l5mh7pp">
              {optional.map(source => <span key={source} className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-medium rounded" data-id="element-70" data-mp="3ewc3us03qzlb">
                  {source}
                </span>)}
            </div>
          </div>}
      </div>;
  };
  return <div className="space-y-4" data-id="element-71" data-mp="3041wjvkohthh">
      {/* Tabs for VM separation (only show when mixing VM with other products) */}
      {showTabs && <div className="bg-white rounded-t-lg border border-b-0 border-gray-200" data-id="element-72" data-mp="1aohgk29gsphx">
          <div className="flex overflow-x-auto border-b border-gray-200 -mb-px scrollbar-hide" data-id="element-73" data-mp="1ffppkx6vu8uk">
            <button onClick={() => setActiveTab('other')} className={`flex-shrink-0 px-4 sm:px-6 py-3 text-xs sm:text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'other' ? 'border-[#0047AB] text-[#0047AB]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-74" data-mp="11ob7qtopodp3">
              Other Products ({otherFields.length})
            </button>
            <button onClick={() => setActiveTab('vm')} className={`flex-shrink-0 px-4 sm:px-6 py-3 text-xs sm:text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'vm' ? 'border-[#0047AB] text-[#0047AB]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-75" data-mp="1kdr9fkc4uc73">
              <span className="hidden sm:inline" data-id="element-76" data-mp="t8dibpr7bky4">
                Volunteer Management ({vmFields.length})
              </span>
              <span className="sm:hidden" data-id="element-77" data-mp="ohexccv9pyoj">VM ({vmFields.length})</span>
            </button>
          </div>
        </div>}

      {/* Summary */}
      <div className={`${showTabs ? 'bg-white border-x border-gray-200 px-4 sm:px-6 py-4' : 'bg-white rounded-lg border border-gray-200 px-4 py-4'}`} data-id="element-78" data-mp="kdz7bzdpewkt">
        {/* Mobile: Stack layout, Desktop: Side by side */}
        <div className="flex flex-col gap-4" data-id="element-79" data-mp="185xjsmow2xzj">
          {/* Field counts - wrap on mobile */}
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm" data-id="element-80" data-mp="136b508tex9om">
            <div className="flex items-center gap-1" data-id="element-81" data-mp="134qdks2ea6uz">
              <span className="font-medium text-gray-700" data-id="element-82" data-mp="y89krkk934bk">Total:</span>
              <span className="text-gray-900 font-semibold" data-id="element-83" data-mp="3nwsftxrmkv84">
                {displayFields.length}
              </span>
            </div>
            <div className="flex items-center gap-1" data-id="element-84" data-mp="3iwnunufrvcbk">
              <span className="inline-flex items-center px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded" data-id="element-85" data-mp="38tse13zjy2by">
                Required
              </span>
              <span className="text-gray-900" data-id="element-86" data-mp="3dt4xder7sr9s">{requiredCount}</span>
            </div>
            <div className="flex items-center gap-1" data-id="element-87" data-mp="3iqdj1pbkndfs">
              <span className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded" data-id="element-88" data-mp="3nietd5p6iu36">
                Recommended
              </span>
              <span className="text-gray-900" data-id="element-89" data-mp="3rmcufly2eeyv">{recommendedCount}</span>
            </div>
            <div className="flex items-center gap-1" data-id="element-90" data-mp="c4czbxv30rc">
              <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-medium rounded" data-id="element-91" data-mp="5cscyn42irxp">
                Optional
              </span>
              <span className="text-gray-900" data-id="element-92" data-mp="274evs1dt3gj3">{optionalCount}</span>
            </div>
          </div>

          {/* Download CSV Button - Full width on mobile */}
          <button onClick={handleDownloadCSV} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-[#0047AB] hover:bg-[#003580] rounded-lg transition-colors" data-id="element-93" data-mp="2c41gevvyrz1x">
            <Download className="w-4 h-4" data-id="element-94" data-mp="2e19nliosxk2v" />
            Download CSV
          </button>
        </div>

        {/* Smart Ask Amounts Note */}
        {hasSmartAskAmounts && <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4" data-id="element-95" data-mp="2n1o7qeq14t4s">
            <p className="text-xs sm:text-sm text-gray-700" data-id="element-96" data-mp="2mzbj5iprnkir">
              <span className="font-semibold text-gray-900" data-id="element-97" data-mp="2i1tl4ndaeppj">Note:</span> Smart
              Ask Amounts requires the last 5 years of giving history in your
              gift file, as well as the most recent 6 months.
            </p>
          </div>}
      </div>

      {/* Mobile Card View - visible on small screens */}
      <div className="md:hidden" data-id="element-98" data-mp="35q32nd3gwrav">
        {/* Constituent Data Section - Mobile */}
        {constituentFields.length > 0 && <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-99" data-mp="3kf4f1domeong">
            <button onClick={() => setShowConstituentData(!showConstituentData)} className="w-full flex items-center justify-between bg-[#0047AB]/10 border-b border-[#0047AB]/20 px-4 py-3 text-left" data-id="element-100" data-mp="3pdz590ox3fhl">
              <h3 className="text-sm font-semibold text-[#0047AB]" data-id="element-101" data-mp="3gc84895bnwu2">
                Constituent Data ({constituentFields.length})
              </h3>
              {showConstituentData ? <ChevronUp className="w-5 h-5 text-[#0047AB]" data-id="element-102" data-mp="36fd81wwiu8iy" /> : <ChevronDown className="w-5 h-5 text-[#0047AB]" data-id="element-103" data-mp="2so8cp706s2ef" />}
            </button>

            {showConstituentData && <div className="divide-y divide-gray-200" data-id="element-104" data-mp="2wptiy9i7tifn">
                {constituentRequiredRecommended.map((field, index) => <MobileFieldCard key={`mobile-constituent-${field.field}-${index}`} field={field} data-id="element-105" data-mp="2irvb7myegk94" data-mpi={index} />)}
                {constituentOptional.length > 0 && <>
                    <div className="bg-gray-50 px-4 py-2 border-t border-gray-200" data-id="element-106" data-mp="2y8tu56veo9o1">
                      <span className="text-xs font-medium text-gray-500 uppercase" data-id="element-107" data-mp="2u4vvsydwn2w1">
                        Optional Constituent Data
                      </span>
                    </div>
                    {constituentOptional.map((field, index) => <MobileFieldCard key={`mobile-constituent-optional-${field.field}-${index}`} field={field} isOptionalSection={true} data-id="element-108" data-mp="2k9lpvzkdybcz" data-mpi={index} />)}
                  </>}
              </div>}
          </div>}

        {/* Other Sections - Mobile */}
        {otherSectionFields.length > 0 && <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mt-4" data-id="element-109" data-mp="1whwv8ck3dasv">
            {/* Required & Recommended Fields Header */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-110" data-mp="c28igg9r3u7t">
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-111" data-mp="gxnvj5zxpm36">
                Other Data ({otherSectionFields.length})
              </h3>
            </div>

            {/* Field Cards */}
            <div className="divide-y divide-gray-200" data-id="element-112" data-mp="pylxzjuq5ljn">
              {otherRequiredRecommended.map((field, index) => <MobileFieldCard key={`mobile-other-${field.field}-${index}`} field={field} data-id="element-113" data-mp="3pcxsy0svq34m" data-mpi={index} />)}
            </div>

            {/* Optional Fields */}
            {otherOptionalFields.length > 0 && <>
                <div className="bg-gray-50 px-4 py-2 border-t border-gray-200" data-id="element-114" data-mp="72m8cfp7yu38">
                  <span className="text-xs font-medium text-gray-500 uppercase" data-id="element-115" data-mp="36nrhmyuf0dsu">
                    Optional
                  </span>
                </div>
                {otherOptionalFields.map((field, index) => <MobileFieldCard key={`mobile-other-optional-${field.field}-${index}`} field={field} isOptionalSection={true} data-id="element-116" data-mp="2i4ev0i7et9nc" data-mpi={index} />)}
              </>}
          </div>}

        {/* Gift Data Section - Mobile */}
        {giftDataFields.length > 0 && <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mt-4" data-id="element-117" data-mp="1zezx2wn0t0n6">
            <button onClick={() => setShowGiftData(!showGiftData)} className="w-full flex items-center justify-between bg-amber-50 border-b border-amber-200 px-4 py-3 text-left" data-id="element-118" data-mp="23f0l98zx8q7f">
              <h3 className="text-sm font-semibold text-amber-900" data-id="element-119" data-mp="2d6dea3xghmf1">
                Gift Data ({giftDataFields.length})
              </h3>
              {showGiftData ? <ChevronUp className="w-5 h-5 text-amber-600" data-id="element-120" data-mp="1kosjovbve7tc" /> : <ChevronDown className="w-5 h-5 text-amber-600" data-id="element-121" data-mp="1d5jdfaynup8i" />}
            </button>

            {showGiftData && <div className="divide-y divide-gray-200" data-id="element-122" data-mp="187q4wb9vxj52">
                {giftDataRequiredRecommended.map((field, index) => <MobileFieldCard key={`mobile-gift-${field.field}-${index}`} field={field} data-id="element-123" data-mp="135sk3clu6ga0" data-mpi={index} />)}
                {giftDataOptional.length > 0 && <>
                    <div className="bg-gray-50 px-4 py-2 border-t border-gray-200" data-id="element-124" data-mp="pfozsr6iown7">
                      <span className="text-xs font-medium text-gray-500 uppercase" data-id="element-125" data-mp="kgukw0bo7s8d">
                        Optional Gift Data
                      </span>
                    </div>
                    {giftDataOptional.map((field, index) => <MobileFieldCard key={`mobile-gift-optional-${field.field}-${index}`} field={field} isOptionalSection={true} data-id="element-126" data-mp="bf4ki4zt9iyg" data-mpi={index} />)}
                  </>}
              </div>}
          </div>}
      </div>

      {/* Desktop Table View - hidden on small screens */}
      <div className="hidden md:block" data-id="element-127" data-mp="3j2rww9hf2khx">
        {/* Constituent Data Section - Desktop */}
        {constituentFields.length > 0 && <div className={`bg-white ${showTabs ? 'border-x' : 'rounded-t-lg border-t border-x'} border-gray-200 overflow-hidden`} data-id="element-128" data-mp="fkgqvpgr3waz">
            {/* Constituent Data Section Header */}
            <div className="bg-[#0047AB]/10 border-b-2 border-[#0047AB]/30 px-4 py-3" data-id="element-129" data-mp="5uowowckd571">
              <h3 className="text-sm font-semibold text-[#0047AB] uppercase tracking-wider" data-id="element-130" data-mp="ati7gh0gui3i">
                Constituent Data
              </h3>
            </div>

            {/* Required & Recommended Constituent Data */}
            {constituentRequiredRecommended.length > 0 && <div className="overflow-x-auto" data-id="element-131" data-mp="13bm745qlsqlt">
                <table className="w-full" data-id="element-132" data-mp="2nz6g5mnby1fa">
                  <thead data-id="element-133" data-mp="2ny3t5pt4oj4n">
                    <tr className="bg-[#0047AB]/5 border-b border-gray-200" data-id="element-134" data-mp="2j1d8sqaaj1df">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-135" data-mp="2izalie0ivmce">
                        Section
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-136" data-mp="2srq3hbbtkaxa">
                        Field
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-137" data-mp="203kiqokrmlxg">
                        Required For
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-138" data-mp="292o0np874vad">
                        Recommended For
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-139" data-mp="3khun37rr8txg">
                    {constituentRequiredRecommended.map((field, index) => {
                const {
                  required,
                  recommended
                } = getSourcesByStatus(field);
                return <tr key={`constituent-${field.field}-${index}`} className="hover:bg-[#0047AB]/5" data-id="element-140" data-mp="tk9pbev8gwzu" data-mpi={index}>
                          <td className="px-4 py-3 text-sm text-gray-700" data-id="element-141" data-mp="12kppaecrplzy">
                            {field.section}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium" data-id="element-142" data-mp="17ihev1pxww0z">
                            {field.field}
                          </td>
                          <td className="px-4 py-3" data-id="element-143" data-mp="1h9c3udukm9q4">
                            <div className="flex flex-wrap gap-1" data-id="element-144" data-mp="1m9q9jp8p97kz">
                              {required.length > 0 ? required.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded" data-id="element-145" data-mp="1q8z2wsztv3g0">
                                    {source}
                                  </span>) : <span className="text-xs text-gray-400" data-id="element-146" data-mp="3uh3kvjmgg8vf">—</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-147" data-mp="3pkmm7vhmqc43">
                            <div className="flex flex-wrap gap-1" data-id="element-148" data-mp="3kqif9c8d6430">
                              {recommended.length > 0 ? recommended.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded" data-id="element-149" data-mp="h2qkrb105xp6">
                                    {source}
                                  </span>) : <span className="text-xs text-gray-400" data-id="element-150" data-mp="1wszt7m4za52y">—</span>}
                            </div>
                          </td>
                        </tr>;
              })}
                  </tbody>
                </table>
              </div>}

            {/* Optional Constituent Data */}
            {constituentOptional.length > 0 && <>
                <div className="bg-gray-100 border-t border-gray-300 px-4 py-2" data-id="element-151" data-mp="1dx9kzh6gccgm">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-152" data-mp="1iw45qonzcb3d">
                    Optional Constituent Data
                  </span>
                </div>
                <div className="overflow-x-auto" data-id="element-153" data-mp="3d79gecxhhns7">
                  <table className="w-full" data-id="element-154" data-mp="3nbg53fe6mroq">
                    <thead data-id="element-155" data-mp="3nc7ozsyf30z6">
                      <tr className="bg-gray-50 border-b border-gray-200" data-id="element-156" data-mp="3icv3bbja7zy6">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-157" data-mp="3ieplii365dyk">
                          Section
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-158" data-mp="3rc028iyr9vav">
                          Field
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" colSpan={2} data-id="element-159" data-mp="4wx6p7ryfvrf">
                          Optional For
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200" data-id="element-160" data-mp="2kr764of05dll">
                      {constituentOptional.map((field, index) => {
                  const {
                    optional
                  } = getSourcesByStatus(field);
                  return <tr key={`constituent-optional-${field.field}-${index}`} className="hover:bg-[#EFF6FF]" data-id="element-161" data-mp="3a70qoao3c3t5" data-mpi={index}>
                            <td className="px-4 py-3 text-sm text-gray-700" data-id="element-162" data-mp="3j0drrzwb6bqb">
                              {field.section}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 font-medium" data-id="element-163" data-mp="2mbobuf48rt6z">
                              {field.field}
                            </td>
                            <td className="px-4 py-3" colSpan={2} data-id="element-164" data-mp="2v98he0950bne">
                              <div className="flex flex-wrap gap-1" data-id="element-165" data-mp="3094ls0lqy53h">
                                {optional.length > 0 ? optional.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded" data-id="element-166" data-mp="fivm9z40dgt2">
                                      {source}
                                    </span>) : <span className="text-xs text-gray-400" data-id="element-167" data-mp="bumfqmy1rqz8">
                                    —
                                  </span>}
                              </div>
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div>
              </>}
          </div>}

        {/* Other Sections - Desktop */}
        {otherSectionFields.length > 0 && <div className="bg-white border-x border-gray-200 overflow-hidden" data-id="element-168" data-mp="1ra3dzys2xa93">
            {/* Other Data Section Header */}
            <div className="bg-gray-100 border-t-2 border-gray-300 px-4 py-3" data-id="element-169" data-mp="2cn0fsf34tcyv">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-170" data-mp="2ckw3p9v2x7ni">
                Other Data
              </h3>
            </div>

            {/* Required & Recommended Other Data */}
            {otherRequiredRecommended.length > 0 && <div className="overflow-x-auto" data-id="element-171" data-mp="2heiesxllh3h7">
                <table className="w-full" data-id="element-172" data-mp="1to4k0zbmkqix">
                  <thead data-id="element-173" data-mp="1towhygsp30cd">
                    <tr className="bg-gray-50 border-b border-gray-200" data-id="element-174" data-mp="1tqh3jgx178y3">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-175" data-mp="1podmmcrlfc5f">
                        Section
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-176" data-mp="1ymfunc9qv527">
                        Field
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-177" data-mp="3a35umdnkr0nd">
                        Required For
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-178" data-mp="10tye1w5bvbtj">
                        Recommended For
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-179" data-mp="rubap4t1fvxu">
                    {otherRequiredRecommended.map((field, index) => {
                const {
                  required,
                  recommended
                } = getSourcesByStatus(field);
                return <tr key={`other-${field.field}-${index}`} className="hover:bg-[#EFF6FF]" data-id="element-180" data-mp="15tb1oj40wmx6" data-mpi={index}>
                          <td className="px-4 py-3 text-sm text-gray-700" data-id="element-181" data-mp="1apt0z3gl3t24">
                            {field.section}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium" data-id="element-182" data-mp="239i271rgekrs">
                            {field.field}
                          </td>
                          <td className="px-4 py-3" data-id="element-183" data-mp="285ccbqd5xi8i">
                            <div className="flex flex-wrap gap-1" data-id="element-184" data-mp="285vn8n4eb6d1">
                              {required.length > 0 ? required.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded" data-id="element-185" data-mp="1y6d428wb7om1">
                                    {source}
                                  </span>) : <span className="text-xs text-gray-400" data-id="element-186" data-mp="1fi831971ehqx">—</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-187" data-mp="16h1nb2c7payq">
                            <div className="flex flex-wrap gap-1" data-id="element-188" data-mp="16icrv4dhemqw">
                              {recommended.length > 0 ? recommended.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded" data-id="element-189" data-mp="3m6s04loti2qw">
                                    {source}
                                  </span>) : <span className="text-xs text-gray-400" data-id="element-190" data-mp="38disapgippah">—</span>}
                            </div>
                          </td>
                        </tr>;
              })}
                  </tbody>
                </table>
              </div>}

            {/* Optional Other Data */}
            {otherOptionalFields.length > 0 && <>
                <div className="bg-gray-100 border-t border-gray-300 px-4 py-2" data-id="element-191" data-mp="q8rg43tbo7su">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-192" data-mp="m6xiy5ic5lxf">
                    Optional
                  </span>
                </div>
                <div className="overflow-x-auto" data-id="element-193" data-mp="v6t14uab6h6y">
                  <table className="w-full" data-id="element-194" data-mp="v6155icsuhg5">
                    <thead data-id="element-195" data-mp="19vp5ag98va2a">
                      <tr className="bg-gray-50 border-b border-gray-200" data-id="element-196" data-mp="19uxls3sx92sd">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-197" data-mp="14ww2bhrl31gd">
                          Section
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-198" data-mp="1iu13p4gcrc5a">
                          Field
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" colSpan={2} data-id="element-199" data-mp="3kpuom7zapz6r">
                          Optional For
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200" data-id="element-200" data-mp="1mekqqflueiyk">
                      {otherOptionalFields.map((field, index) => {
                  const {
                    optional
                  } = getSourcesByStatus(field);
                  return <tr key={`other-optional-${field.field}-${index}`} className="hover:bg-[#EFF6FF]" data-id="element-201" data-mp="1cfdgi1aen4fo" data-mpi={index}>
                            <td className="px-4 py-3 text-sm text-gray-700" data-id="element-202" data-mp="13gi6ee48pa73">
                              {field.section}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 font-medium" data-id="element-203" data-mp="2xthsmq34l2nc">
                              {field.field}
                            </td>
                            <td className="px-4 py-3" colSpan={2} data-id="element-204" data-mp="31ua4lr3dtlnb">
                              <div className="flex flex-wrap gap-1" data-id="element-205" data-mp="enouyovl4c9b">
                                {optional.length > 0 ? optional.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded" data-id="element-206" data-mp="5l5wegyu0lsv">
                                      {source}
                                    </span>) : <span className="text-xs text-gray-400" data-id="element-207" data-mp="oj8m6xz7emrq">
                                    —
                                  </span>}
                              </div>
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div>
              </>}
          </div>}

        {/* Gift Data Section - Desktop */}
        {giftDataFields.length > 0 && <div className={`bg-white ${constituentFields.length === 0 && otherSectionFields.length === 0 ? 'rounded-lg border' : 'rounded-b-lg border-x border-b'} border-gray-200 overflow-hidden`} data-id="element-208" data-mp="1tjygiw92lszk">
            {/* Gift Data Section Header */}
            <div className="bg-amber-50 border-t-2 border-amber-300 px-4 py-3" data-id="element-209" data-mp="22iaitvjauf7u">
              <h3 className="text-sm font-semibold text-amber-900 uppercase tracking-wider" data-id="element-210" data-mp="27hwwu76ybzhp">
                Gift Data
              </h3>
            </div>

            {/* Required & Recommended Gift Data */}
            {giftDataRequiredRecommended.length > 0 && <div className="overflow-x-auto" data-id="element-211" data-mp="2lbnuah7ersx2">
                <table className="w-full" data-id="element-212" data-mp="2l8tamrw7yc4p">
                  <thead data-id="element-213" data-mp="2q9ftzxeaojbt">
                    <tr className="bg-amber-50/50 border-b border-gray-200" data-id="element-214" data-mp="2qar1a76y897o">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-215" data-mp="3bk4o09bkd8nf">
                        Field
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-216" data-mp="3litxpetb3msl">
                        Required For
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-217" data-mp="3gi64cmhlutv7">
                        Recommended For
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-218" data-mp="32khl1j36rus9">
                    {giftDataRequiredRecommended.map((field, index) => {
                const {
                  required,
                  recommended
                } = getSourcesByStatus(field);
                return <tr key={`gift-${field.field}-${index}`} className="hover:bg-amber-50/30" data-id="element-219" data-mp="2553pbvv5mcd5" data-mpi={index}>
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium" data-id="element-220" data-mp="kl00oras8851">
                            {field.field}
                          </td>
                          <td className="px-4 py-3" data-id="element-221" data-mp="apy9zr0uyy0c">
                            <div className="flex flex-wrap gap-1" data-id="element-222" data-mp="fnq50twsv08r">
                              {required.length > 0 ? required.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded" data-id="element-223" data-mp="13bhr2p856rn8">
                                    {source}
                                  </span>) : <span className="text-xs text-gray-400" data-id="element-224" data-mp="3j46yoo09f4n8">—</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-225" data-mp="1shi7a7denw4x">
                            <div className="flex flex-wrap gap-1" data-id="element-226" data-mp="1xbcv9n01mx9t">
                              {recommended.length > 0 ? recommended.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded" data-id="element-227" data-mp="1oh81ovtiz7ql">
                                    {source}
                                  </span>) : <span className="text-xs text-gray-400" data-id="element-228" data-mp="2777i6ubaveie">—</span>}
                            </div>
                          </td>
                        </tr>;
              })}
                  </tbody>
                </table>
              </div>}

            {/* Optional Gift Data */}
            {giftDataOptional.length > 0 && <>
                <div className="bg-gray-100 border-t border-gray-300 px-4 py-2" data-id="element-229" data-mp="jcue7zi4lh4k">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-230" data-mp="x9pyko3tuuzx">
                    Optional Gift Data
                  </span>
                </div>
                <div className="overflow-x-auto" data-id="element-231" data-mp="4mn4c866p6fh">
                  <table className="w-full" data-id="element-232" data-mp="k8m9qacaap9">
                    <thead data-id="element-233" data-mp="ljldcsuaua1">
                      <tr className="bg-gray-50 border-b border-gray-200" data-id="element-234" data-mp="i5kya3qkv58">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-235" data-mp="ed7xhg339ir0">
                          Field
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" colSpan={2} data-id="element-236" data-mp="3hzos3sk4l1j0">
                          Optional For
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200" data-id="element-237" data-mp="1lvqzdrgtsny8">
                      {giftDataOptional.map((field, index) => {
                  const {
                    optional
                  } = getSourcesByStatus(field);
                  return <tr key={`gift-optional-${field.field}-${index}`} className="hover:bg-[#EFF6FF]" data-id="element-238" data-mp="xzwgwe7ziwu4" data-mpi={index}>
                            <td className="px-4 py-3 text-sm text-gray-900 font-medium" data-id="element-239" data-mp="17y2h8re57uwv">
                              {field.field}
                            </td>
                            <td className="px-4 py-3" colSpan={2} data-id="element-240" data-mp="1c0qst6u8rfxj">
                              <div className="flex flex-wrap gap-1" data-id="element-241" data-mp="2jbxyc5olydqq">
                                {optional.length > 0 ? optional.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded" data-id="element-242" data-mp="brhddbfhzy41">
                                      {source}
                                    </span>) : <span className="text-xs text-gray-400" data-id="element-243" data-mp="3p7f8tbv39w97">
                                    —
                                  </span>}
                              </div>
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div>
              </>}
          </div>}
      </div>
    </div>;
}